// Regras de visibilidade dos requerimentos por perfil.
// ADMIN: tudo | MODERADOR: toda a cidade | GESTOR: toda a secretaria (e sub locais)
// COORDENADOR: apenas os sub locais atribuídos.

import type { AppUser } from '@/contexts/AuthContext'

interface ProtocoloEscopo {
  cidadeId?: string
  secretariaId?: string
  subLocalId?: string
}

/** Filtro para enviar à base de dados, restringindo os requerimentos ao âmbito do utilizador. */
export function buildEscopoFilter(user: AppUser | null): Record<string, any> {
  const filter: Record<string, any> = {}
  if (!user)
    return filter

  if (user.role === 'MODERADOR' && user.cidadeId) {
    filter.cidadeId = user.cidadeId
    return filter
  }

  if (user.role === 'GESTOR') {
    if (user.cidadeId)
      filter.cidadeId = user.cidadeId
    if (user.secretariaId)
      filter.secretariaId = user.secretariaId
    return filter
  }

  if (user.role === 'COORDENADOR') {
    if (user.cidadeId)
      filter.cidadeId = user.cidadeId
    if (user.secretariaId)
      filter.secretariaId = user.secretariaId
    if (user.subLocalIds?.length)
      filter.subLocalId = { $in: user.subLocalIds }
    return filter
  }

  return filter
}

/** Verifica no cliente se um requerimento pertence ao âmbito do utilizador. */
export function pertenceAoEscopo(p: ProtocoloEscopo, user: AppUser | null): boolean {
  if (!user || user.role === 'ADMIN')
    return true

  if (user.cidadeId && p.cidadeId && p.cidadeId !== user.cidadeId)
    return false

  if (user.role === 'MODERADOR')
    return true

  if (user.role === 'GESTOR') {
    if (user.secretariaId && p.secretariaId !== user.secretariaId)
      return false
    return true
  }

  if (user.role === 'COORDENADOR') {
    const ids = user.subLocalIds || []
    if (!ids.length)
      return false
    return Boolean(p.subLocalId && ids.includes(p.subLocalId))
  }

  return true
}

export function filtrarPorEscopo<T extends ProtocoloEscopo>(lista: T[], user: AppUser | null): T[] {
  return lista.filter(p => pertenceAoEscopo(p, user))
}

/** Texto curto que explica o âmbito de visualização do utilizador. */
export function escopoLabel(user: AppUser | null, cidadeNome?: string, secretariaNome?: string): string {
  if (!user)
    return ''
  if (user.role === 'ADMIN')
    return 'A visualizar requerimentos de todas as cidades.'
  if (user.role === 'MODERADOR')
    return `A visualizar todos os requerimentos de ${cidadeNome || 'sua cidade'}, em todas as secretarias e sub locais.`
  if (user.role === 'GESTOR')
    return `A visualizar todos os requerimentos da secretaria${secretariaNome ? ` ${secretariaNome}` : ' atribuída'}, incluindo os seus sub locais.`
  return 'A visualizar os requerimentos dos sub locais atribuídos a si.'
}
